'use client';

import { eyelidPositions, eyePaths, eyeShapeTransforms, mouthPaths } from './legacyPaths';
import { useEyeTracking } from './useEyeTracking';
import { DEFAULT_EXPRESSION, type RainaExpression } from './useRainaAnimation';

/** Mouth rendering configuration for data-driven loop */
interface MouthShapeConfig {
  expression: RainaExpression;
  d: string;
  filled: boolean;
}

const MOUTH_SHAPES: MouthShapeConfig[] = [
  { expression: 'neutral', d: mouthPaths.neutral, filled: false },
  { expression: 'happy', d: mouthPaths.smile, filled: false },
  { expression: 'listening', d: mouthPaths.listening, filled: false },
  { expression: 'thinking', d: mouthPaths.thinking, filled: false },
  { expression: 'surprised', d: mouthPaths.surprised, filled: true },
  { expression: 'concerned', d: mouthPaths.concerned, filled: false },
  { expression: 'proud', d: mouthPaths.proud, filled: false },
  { expression: 'confused', d: mouthPaths.confused, filled: false },
  { expression: 'tired', d: mouthPaths.tired, filled: true },
  { expression: 'curious', d: mouthPaths.curious, filled: false },
  { expression: 'apologetic', d: mouthPaths.apologetic, filled: true },
  { expression: 'impressed', d: mouthPaths.impressed, filled: false },
  { expression: 'focused', d: mouthPaths.focused, filled: false },
  { expression: 'excited', d: mouthPaths.excited, filled: true },
  { expression: 'celebrating', d: mouthPaths.celebrating, filled: true },
  { expression: 'patient', d: mouthPaths.patient, filled: false },
  { expression: 'skeptical', d: mouthPaths.skeptical, filled: false },
];

const STROKE_MOUTH_PROPS = {
  stroke: '#33116F',
  strokeLinecap: 'round' as const,
  strokeMiterlimit: '10',
  strokeWidth: '6.55515',
};

/** Transform origins at eye centers for natural scaling */
const RIGHT_EYE_ORIGIN = '164px 55px';
const LEFT_EYE_ORIGIN = '33px 55px';

/** Eye area positioning within the full body viewBox */
const EYE_AREA_POSITION = { left: '26.87%', top: '35.17%', width: '46.03%', height: '15.74%' };

/** Saccade transition duration for idle wander movements */
const WANDER_SACCADE_DURATION_MS = 150;

/** Expression transition duration for smooth facial feature changes */
const EXPRESSION_TRANSITION_DURATION_MS = 300;

interface LegacyInteractiveEyesProps {
  svgId: string;
  enabled: boolean;
  expression?: RainaExpression;
  isBlinking?: boolean;
  idleAnimations?: boolean;
  onBlinkEnd?: () => void;
}

export function LegacyInteractiveEyes({
  svgId,
  enabled,
  expression = DEFAULT_EXPRESSION,
  isBlinking = false,
  idleAnimations = true,
  onBlinkEnd,
}: LegacyInteractiveEyesProps) {
  const id = (name: string) => `${svgId}${name}`;
  const { containerRef, eyeOffset, isIdle } = useEyeTracking({ enabled, enableWander: idleAnimations });

  // Instant following when tracking; saccade-like ease-out when idle wandering
  const eyeTransition = isIdle ? `transform ${WANDER_SACCADE_DURATION_MS}ms ease-out` : 'none';

  const shapeTransform = eyeShapeTransforms[expression] || eyeShapeTransforms.neutral;
  const { eyeWhite, pupil } = shapeTransform;

  const eyelidPos = eyelidPositions[expression] || eyelidPositions.neutral;

  // Blink keyframe variant matched to eyelid resting position (see raina.css)
  const getBlinkAnimation = (position: number) => {
    if (position <= -90) return 'motion-safe:animate-[raina-blink_0.25s_ease-in-out]';
    if (position <= -80) return 'motion-safe:animate-[raina-blink-alert_0.25s_ease-in-out]';
    if (position <= -70) return 'motion-safe:animate-[raina-blink-squint_0.25s_ease-in-out]';
    if (position <= -60) return 'motion-safe:animate-[raina-blink-heavy_0.25s_ease-in-out]';
    return 'motion-safe:animate-[raina-blink-sleepy_0.25s_ease-in-out]';
  };

  const rightBlinkAnimation = getBlinkAnimation(eyelidPos.right);
  const leftBlinkAnimation = getBlinkAnimation(eyelidPos.left);

  return (
    <div ref={containerRef} className="absolute pointer-events-none" style={EYE_AREA_POSITION}>
      <div className="absolute inset-[-3.38%_0%] pointer-events-none">
        <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 197 103.567">
          <defs>
            <radialGradient
              cx="0"
              cy="0"
              gradientTransform="matrix(-22.2875 32.4874 32.1104 23.0024 178.646 38.982)"
              gradientUnits="userSpaceOnUse"
              id={id('rightEyeGrad')}
              r="1"
            >
              <stop stopColor="#C2A0FF" />
              <stop offset="1" stopColor="#33116F" />
            </radialGradient>
            <radialGradient
              cx="0"
              cy="0"
              gradientTransform="matrix(-16.5501 24.3655 23.8443 17.2518 175.327 35.1556)"
              gradientUnits="userSpaceOnUse"
              id={id('rightPupilGrad')}
              r="1"
            >
              <stop stopColor="#C2A0FF" />
              <stop offset="1" stopColor="#33116F" />
            </radialGradient>
            <radialGradient
              cx="0"
              cy="0"
              gradientTransform="matrix(-22.2875 32.4874 32.1104 23.0024 47.8526 38.6656)"
              gradientUnits="userSpaceOnUse"
              id={id('leftEyeGrad')}
              r="1"
            >
              <stop stopColor="#C2A0FF" />
              <stop offset="1" stopColor="#33116F" />
            </radialGradient>
            <radialGradient
              cx="0"
              cy="0"
              gradientTransform="matrix(-16.5501 24.3655 23.8443 17.2518 44.5341 34.8373)"
              gradientUnits="userSpaceOnUse"
              id={id('leftPupilGrad')}
              r="1"
            >
              <stop stopColor="#C2A0FF" />
              <stop offset="1" stopColor="#33116F" />
            </radialGradient>
            <clipPath id={id('rightEyeClip')}>
              <path d={eyePaths.rightEyeWhite} />
            </clipPath>
            <clipPath id={id('leftEyeClip')}>
              <path d={eyePaths.leftEyeWhite} />
            </clipPath>
          </defs>

          {/* Mouth */}
          <g className="raina-mouth">
            {MOUTH_SHAPES.map(({ expression: expr, d, filled }) => (
              <path
                key={expr}
                d={d}
                {...(filled ? { fill: '#33116F' } : STROKE_MOUTH_PROPS)}
                style={{ opacity: expression === expr ? 1 : 0, transition: `opacity ${EXPRESSION_TRANSITION_DURATION_MS}ms ease-out` }}
              />
            ))}
            {/* Talking — two alternating mouth shapes */}
            {expression === 'talking' && (
              <>
                <path d={mouthPaths.talkClosed} fill="#33116F" className="motion-safe:animate-[raina-talk_0.3s_ease-in-out_infinite]" />
                <path
                  d={mouthPaths.talkOpen}
                  fill="#33116F"
                  className="motion-safe:animate-[raina-talk_0.3s_ease-in-out_infinite_150ms]"
                  style={{ opacity: 0 }}
                />
              </>
            )}
          </g>

          {/* Right Eye */}
          <g
            style={{
              transform: `scaleX(${eyeWhite.scaleX}) scaleY(${eyeWhite.scaleY}) translateY(${eyeWhite.translateY}px)`,
              transformOrigin: RIGHT_EYE_ORIGIN,
              transition: `transform ${EXPRESSION_TRANSITION_DURATION_MS}ms ease-out`,
            }}
          >
            <path d={eyePaths.rightEyeWhite} fill={`url(#${id('rightEyeGrad')})`} />
            <path d={eyePaths.rightEyeWhite} fill="white" />
          </g>
          <g clipPath={`url(#${id('rightEyeClip')})`}>
            <g
              style={{
                transform: `translate(${eyeOffset.x}px, ${eyeOffset.y + pupil.translateY}px) scale(${pupil.scale})`,
                transformOrigin: RIGHT_EYE_ORIGIN,
                transition: eyeTransition,
              }}
            >
              <path d={eyePaths.rightPupil} fill={`url(#${id('rightPupilGrad')})`} />
              <path d={eyePaths.rightPupil} fill="#33116F" />
            </g>
          </g>

          {/* Left Eye */}
          <g
            style={{
              transform: `scaleX(${eyeWhite.scaleX}) scaleY(${eyeWhite.scaleY}) translateY(${eyeWhite.translateY}px)`,
              transformOrigin: LEFT_EYE_ORIGIN,
              transition: `transform ${EXPRESSION_TRANSITION_DURATION_MS}ms ease-out`,
            }}
          >
            <path d={eyePaths.leftEyeWhite} fill={`url(#${id('leftEyeGrad')})`} />
            <path d={eyePaths.leftEyeWhite} fill="white" />
          </g>
          <g clipPath={`url(#${id('leftEyeClip')})`}>
            <g
              style={{
                transform: `translate(${eyeOffset.x}px, ${eyeOffset.y + pupil.translateY}px) scale(${pupil.scale})`,
                transformOrigin: LEFT_EYE_ORIGIN,
                transition: eyeTransition,
              }}
            >
              <path d={eyePaths.leftPupil} fill={`url(#${id('leftPupilGrad')})`} />
              <path d={eyePaths.leftPupil} fill="#33116F" />
            </g>
          </g>

          {/* Eyelids (rendered last so they appear on top during blink) */}
          <g clipPath={`url(#${id('rightEyeClip')})`}>
            <rect
              x="120"
              y="0"
              width="90"
              height="100"
              fill="#F6C8BB"
              className={isBlinking ? rightBlinkAnimation : ''}
              style={{
                transform: `translateY(${eyelidPos.right}px)`,
                transition: isBlinking ? undefined : `transform ${EXPRESSION_TRANSITION_DURATION_MS}ms ease-out`,
                willChange: 'transform',
              }}
              onAnimationEnd={(e) => {
                e.stopPropagation();
                onBlinkEnd?.();
              }}
            />
          </g>
          <g clipPath={`url(#${id('leftEyeClip')})`}>
            <rect
              x="-10"
              y="0"
              width="90"
              height="100"
              fill="#F6C8BB"
              className={isBlinking ? leftBlinkAnimation : ''}
              style={{
                transform: `translateY(${eyelidPos.left}px)`,
                transition: isBlinking ? undefined : `transform ${EXPRESSION_TRANSITION_DURATION_MS}ms ease-out`,
                willChange: 'transform',
              }}
            />
          </g>
        </svg>
      </div>
    </div>
  );
}
